import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { ExcelTaskItem } from "../hooks/useExcelTaskPolling";
import { pages } from "../constants/PagePaths";
import { ExcelFixFigure, ExcelFixGuide } from "./excelFixGuides";
import { getTenantInfo, ownerLabel, supportLabel } from "../contexts/TenantContext";

/**
 * 엑셀자동화 실패 작업의 "조치 방법" 모달.
 *
 * - document.body 에 포털로 띄운다(작업 패널은 position:fixed 작은 카드라 그 안에 넣으면 잘린다).
 * - ESC·배경 클릭·닫기 버튼으로 닫힌다.
 * - 그림은 외부 이미지 파일 없이 HTML 표·SVG 로 그린다(빌드 산출물만 scp 로 배포하는 구조).
 */

interface ExcelFixGuideModalProps {
  task: ExcelTaskItem;
  guide: ExcelFixGuide;
  serviceLabel: string;
  onClose: () => void;
  /** 같은 파일로 다시 실행(기존 재시도 동작). guide.retryHint === "same_file" 일 때만 버튼이 보인다. */
  onRetry?: () => void;
}

const STYLE_ID = "excel-fix-guide-styles";

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes excelFixGuideFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes excelFixGuidePopIn {
      from { opacity: 0; transform: translateY(16px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .excel-fix-guide-backdrop {
      position: fixed; inset: 0; z-index: 2000;
      background: rgba(17, 24, 39, 0.55);
      display: flex; align-items: center; justify-content: center;
      padding: 16px;
      animation: excelFixGuideFadeIn 0.15s ease-out;
    }
    .excel-fix-guide-card {
      background: #fff; border-radius: 16px;
      width: 100%; max-width: 560px; max-height: calc(100vh - 32px);
      display: flex; flex-direction: column; overflow: hidden;
      box-shadow: 0 20px 60px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.1);
      animation: excelFixGuidePopIn 0.2s ease-out;
      font-family: inherit;
    }
    .excel-fix-guide-body {
      overflow-y: auto; -webkit-overflow-scrolling: touch;
      padding: 16px 20px 8px;
    }
    .excel-fix-guide-close:hover { background: #f0f0f0 !important; color: #333 !important; }
    .excel-fix-guide-steps { margin: 8px 0 0; padding-left: 0; list-style: none; counter-reset: fixstep; }
    .excel-fix-guide-steps > li {
      position: relative; padding-left: 32px; margin-bottom: 10px;
      font-size: 13px; line-height: 1.55; color: #333; word-break: keep-all;
    }
    .excel-fix-guide-steps > li::before {
      counter-increment: fixstep; content: counter(fixstep);
      position: absolute; left: 0; top: 1px;
      width: 22px; height: 22px; border-radius: 50%;
      background: #FFF3E0; color: #E65100; font-weight: 700; font-size: 12px;
      display: flex; align-items: center; justify-content: center;
    }
    .excel-fix-guide-figure {
      margin: 6px 0 12px; padding: 12px; border: 1px dashed #cfd8dc; border-radius: 10px;
      background: #fafbfc; overflow-x: auto;
    }
    .excel-fix-guide-caption { font-size: 11.5px; color: #607d8b; line-height: 1.5; margin-top: 8px; word-break: keep-all; }
    .excel-fix-guide-table { border-collapse: collapse; font-size: 11px; min-width: 420px; }
    .excel-fix-guide-table th, .excel-fix-guide-table td {
      border: 1px solid #b0bec5; padding: 4px 6px; text-align: center; white-space: nowrap; color: #37474f;
    }
    .excel-fix-guide-table th { background: #eceff1; font-weight: 600; }
    .excel-fix-guide-table th.req { background: #FFE0B2; color: #BF360C; }
    .excel-fix-guide-table th.req-sub { background: #FFF3E0; color: #BF360C; font-weight: 600; }
    .excel-fix-guide-table td.data { color: #90a4ae; background: #fff; }
    .excel-fix-guide-btn {
      border: none; border-radius: 8px; padding: 9px 14px; font-size: 13px; font-weight: 600;
      cursor: pointer; line-height: 1.2; transition: background-color 0.15s, border-color 0.15s;
    }
    .excel-fix-guide-btn-primary { background: #2196F3; color: #fff; }
    .excel-fix-guide-btn-primary:hover { background: #1976D2; }
    .excel-fix-guide-btn-accent { background: #FF8F00; color: #fff; }
    .excel-fix-guide-btn-accent:hover { background: #EF6C00; }
    .excel-fix-guide-btn-ghost { background: #fff; color: #555; border: 1px solid #ddd; }
    .excel-fix-guide-btn-ghost:hover { border-color: #bbb; color: #222; }
    .excel-fix-guide-tab { display: inline-flex; align-items: center; gap: 4px; padding: 4px 9px; border: 1px solid #b0bec5; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 11px; color: #546e7a; background: #eceff1; }
    .excel-fix-guide-tab.on { background: #fff; color: #1b5e20; font-weight: 700; border-color: #66bb6a; }
    .excel-fix-guide-tab.off { color: #9e9e9e; text-decoration: line-through; }
    @media (max-width: 480px) {
      .excel-fix-guide-backdrop { padding: 8px; align-items: flex-end; }
      .excel-fix-guide-card { max-height: calc(100vh - 16px); border-radius: 14px; }
      .excel-fix-guide-body { padding: 12px 14px 4px; }
    }
  `;
  document.head.appendChild(style);
}

/* ───────────── 그림 ───────────── */

/** 내역서 2줄 머리글 구조 — 꼭 있어야 하는 열을 색으로 표시 */
const HeaderStructureFigure: React.FC = () => (
  <div className="excel-fix-guide-figure">
    <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 8 }}>내역서 머리글은 이렇게 생겨야 해요</div>
    <table className="excel-fix-guide-table" aria-label="내역서 머리글 구조 예시">
      <thead>
        <tr>
          <th className="req" rowSpan={2}>품명</th>
          <th className="req" rowSpan={2}>규격</th>
          <th className="req" rowSpan={2}>단위</th>
          <th className="req" rowSpan={2}>수량</th>
          <th className="req" colSpan={2}>재료비</th>
          <th className="req" colSpan={2}>노무비</th>
          <th className="req" colSpan={2}>경비</th>
          <th className="req" colSpan={2}>합계</th>
          <th rowSpan={2}>비고</th>
        </tr>
        <tr>
          <th className="req-sub">단가</th>
          <th className="req-sub">금액</th>
          <th className="req-sub">단가</th>
          <th className="req-sub">금액</th>
          <th className="req-sub">단가</th>
          <th className="req-sub">금액</th>
          <th className="req-sub">단가</th>
          <th className="req-sub">금액</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="data">레미콘</td>
          <td className="data">25-24-150</td>
          <td className="data">m³</td>
          <td className="data">120</td>
          <td className="data">85,000</td>
          <td className="data">10,200,000</td>
          <td className="data">0</td>
          <td className="data">0</td>
          <td className="data">0</td>
          <td className="data">0</td>
          <td className="data">85,000</td>
          <td className="data">10,200,000</td>
          <td className="data"></td>
        </tr>
        <tr>
          <td className="data">철근 가공조립</td>
          <td className="data">보통</td>
          <td className="data">ton</td>
          <td className="data">8</td>
          <td className="data">0</td>
          <td className="data">0</td>
          <td className="data">420,000</td>
          <td className="data">3,360,000</td>
          <td className="data">0</td>
          <td className="data">0</td>
          <td className="data">420,000</td>
          <td className="data">3,360,000</td>
          <td className="data"></td>
        </tr>
      </tbody>
    </table>
    <div className="excel-fix-guide-caption">
      <span style={{ display: "inline-block", width: 10, height: 10, background: "#FFE0B2", border: "1px solid #FFB74D", verticalAlign: -1, marginRight: 4 }} />
      색칠한 칸이 꼭 있어야 하는 머리글입니다. 재료비·노무비·경비·합계는 각각 단가/금액 두 칸으로 나뉘어 있어야 하고, 머리글은 데이터 위에
      한 덩어리로 있어야 합니다. 품명 대신 명칭·공종명·품목, 규격 대신 재질 및 규격, 재료비 대신 직접재료비 같은 표기도 인식합니다.
    </div>
  </div>
);

/** 처리할 시트만 고르기 — 시트 탭 모양 */
const SheetSelectFigure: React.FC = () => {
  const tabs: { name: string; on: boolean }[] = [
    { name: "갑지", on: false },
    { name: "목차", on: false },
    { name: "원가계산서", on: true },
    { name: "집계표", on: true },
    { name: "내역서", on: true },
    { name: "수량산출서", on: false },
    { name: "단가산출", on: false },
  ];
  return (
    <div className="excel-fix-guide-figure">
      <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 8 }}>처리할 시트만 체크하세요</div>
      <div style={{ display: "flex", gap: 3, alignItems: "flex-end", borderBottom: "2px solid #66bb6a", paddingBottom: 0, minWidth: 420 }}>
        {tabs.map((t) => (
          <span key={t.name} className={`excel-fix-guide-tab ${t.on ? "on" : "off"}`}>
            {t.on ? (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M2 5.5L4 7.5L8 3" fill="none" stroke="#2e7d32" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke="#bdbdbd" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            )}
            {t.name}
          </span>
        ))}
      </div>
      <div className="excel-fix-guide-caption">
        내역서·집계표(필요하면 원가계산서)만 체크하고, 갑지·목차·수량산출서·단가산출처럼 내역 표가 아닌 시트는 체크를 풉니다.
      </div>
    </div>
  );
};

/** Ctrl+End 로 멀리 떨어진 셀 찾기 */
const CtrlEndFigure: React.FC = () => (
  <div className="excel-fix-guide-figure">
    <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 8 }}>Ctrl+End 를 누르면 '마지막 셀' 로 이동합니다</div>
    <svg width="420" height="150" viewBox="0 0 420 150" role="img" aria-label="데이터는 왼쪽 위에 있는데 멀리 떨어진 오른쪽 아래 셀에 값이 남아 범위가 커진 모습">
      {/* 격자 */}
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={`v${i}`} x1={20 + i * 38} y1={16} x2={20 + i * 38} y2={140} stroke="#e0e0e0" strokeWidth="1" />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`h${i}`} x1={20} y1={16 + i * 18} x2={400} y2={16 + i * 18} stroke="#e0e0e0" strokeWidth="1" />
      ))}
      {/* 실제 데이터 블록 */}
      <rect x="20" y="16" width="152" height="72" fill="#E3F2FD" stroke="#42A5F5" strokeWidth="1.5" />
      <text x="96" y="56" textAnchor="middle" fontSize="11" fill="#1565C0" fontWeight="700">실제 내역 (여기까지)</text>
      {/* 멀리 떨어진 셀 */}
      <rect x="362" y="106" width="38" height="18" fill="#FFCDD2" stroke="#E53935" strokeWidth="1.5" />
      <text x="381" y="119" textAnchor="middle" fontSize="10" fill="#B71C1C" fontWeight="700">1</text>
      <text x="300" y="100" textAnchor="middle" fontSize="10.5" fill="#B71C1C">잘못 들어간 값·수식·서식</text>
      <path d="M300 104 L358 112" fill="none" stroke="#E53935" strokeWidth="1.2" markerEnd="url(#fixArrow)" />
      {/* 범위 테두리 */}
      <rect x="20" y="16" width="380" height="108" fill="none" stroke="#E53935" strokeWidth="1" strokeDasharray="4 3" />
      <text x="24" y="136" fontSize="10" fill="#B71C1C">← 이 점선 전체가 '데이터 범위' 로 잡혀 한도를 넘깁니다</text>
      <defs>
        <marker id="fixArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0 0L6 3L0 6z" fill="#E53935" />
        </marker>
      </defs>
    </svg>
    <div className="excel-fix-guide-caption">
      마지막 셀이 실제 내역보다 훨씬 아래·오른쪽이면 그 사이 행·열을 통째로 삭제(지우기가 아니라 삭제)하고 저장하세요.
    </div>
  </div>
);

/** 데이터 › 쿼리 및 연결 › 연결 끊기 경로 */
const ExternalLinkFigure: React.FC = () => {
  const crumbs = ["데이터 탭", "쿼리 및 연결 (연결 편집)", "외부 연결 선택", "연결 끊기"];
  return (
    <div className="excel-fix-guide-figure">
      <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 8 }}>엑셀 메뉴 경로</div>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        {crumbs.map((c, i) => (
          <React.Fragment key={c}>
            {i > 0 && (
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M4 2L8 6L4 10" fill="none" stroke="#90a4ae" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 11.5,
                fontWeight: 600,
                background: i === crumbs.length - 1 ? "#FFE0B2" : "#eceff1",
                color: i === crumbs.length - 1 ? "#BF360C" : "#37474f",
                border: `1px solid ${i === crumbs.length - 1 ? "#FFB74D" : "#cfd8dc"}`,
              }}
            >
              {c}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="excel-fix-guide-caption">
        연결을 끊으면 다른 파일을 가리키던 수식이 현재 값으로 바뀝니다. 엑셀 버전에 따라 '연결 편집' 버튼이 '데이터' 탭의 '쿼리 및 연결' 묶음 안에 있습니다.
      </div>
    </div>
  );
};

const FIGURES: Record<ExcelFixFigure, React.FC> = {
  header_structure: HeaderStructureFigure,
  sheet_select: SheetSelectFigure,
  ctrl_end: CtrlEndFigure,
  external_link: ExternalLinkFigure,
};

/* ───────────── 모달 ───────────── */

/** 발주처(B2B) 모드: "이용권 구매" 안내는 맞지 않는다(결제 페이지가 없고 기능은 발주처가 계정별로 연다). 담당자 요청 안내로 바꾼다. */
const adjustGuideForTenant = (guide: ExcelFixGuide, serviceLabel: string): ExcelFixGuide => {
  const tenant = getTenantInfo();
  if (!tenant || guide.kind !== "no_permission") return guide;
  return {
    ...guide,
    title: "이 계정에 열리지 않은 기능이에요",
    summary: `${ownerLabel(tenant)} 담당자가 계정별로 열어 주는 기능입니다. 열리면 같은 파일로 다시 실행하면 됩니다.`,
    steps: [`담당자에게 "${serviceLabel}" 기능을 열어 달라고 요청합니다.`, "열린 뒤 같은 파일로 다시 실행합니다."],
    linkTo: undefined,
  };
};

const ExcelFixGuideModal: React.FC<ExcelFixGuideModalProps> = ({ task, guide: guideProp, serviceLabel, onClose, onRetry }) => {
  const guide = adjustGuideForTenant(guideProp, serviceLabel);
  const history = useHistory();
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = `excel-fix-guide-title-${task.id}`;

  // onClose 는 부모가 매 렌더마다 새로 만들 수 있으므로 ref 로 받아 effect 를 한 번만 건다.
  // (의존성에 넣으면 cleanup 이 매번 돌아 body overflow 복원값이 "hidden" 으로 꼬인다.)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => ensureStyles(), []);

  // ESC 로 닫기 + 열려 있는 동안 뒤 페이지 스크롤 잠금
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cardRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const Figure = guide.figure ? FIGURES[guide.figure] : null;
  // 내역서 인식 안 됨은 1단계(시트 고르기) 아래에 시트 탭 그림을 하나 더 넣는다.
  const extraSheetFigure = guide.kind === "statement_not_found";
  const canRetrySameFile = guide.retryHint === "same_file" && !!onRetry && (task.status === "failed" || task.status === "cancelled");

  const modal = (
    <div
      className="excel-fix-guide-backdrop"
      onMouseDown={(e) => {
        // 배경(딤) 자체를 눌렀을 때만 닫는다 — 카드 안에서 드래그하다 밖에서 놓아도 안 닫히게.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className="excel-fix-guide-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ outline: "none" }}
      >
        {/* 헤더 */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "16px 16px 12px 20px", borderBottom: "1px solid #f0f0f0" }}>
          <span
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "#FFF3E0",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-hidden="true"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M9 18h6M10 21h4" stroke="#EF6C00" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M12 3a6.5 6.5 0 0 0-3.9 11.7c.6.5.9 1.1.9 1.8V17h6v-.5c0-.7.3-1.3.9-1.8A6.5 6.5 0 0 0 12 3z" stroke="#EF6C00" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#EF6C00", letterSpacing: "0.2px", marginBottom: 2 }}>조치 방법</div>
            <h2 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.35, wordBreak: "keep-all" }}>
              {guide.title}
            </h2>
            <div style={{ marginTop: 6, fontSize: 11.5, color: "#777", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
              {serviceLabel && (
                <span style={{ padding: "1px 7px", borderRadius: 10, background: "#f1f3f5", color: "#555", fontWeight: 600 }}>{serviceLabel}</span>
              )}
              <span style={{ wordBreak: "break-all" }} title={task.file_name || ""}>
                {task.file_name || "파일"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="excel-fix-guide-close"
            onClick={onClose}
            aria-label="조치 방법 닫기"
            style={{
              flexShrink: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              width: 30,
              height: 30,
              borderRadius: 8,
              color: "#999",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* 본문(스크롤) */}
        <div className="excel-fix-guide-body">
          {/* 서버 안내문 원문 */}
          {task.error_message && (
            <div
              style={{
                background: "#f5f6f8",
                border: "1px solid #e6e8eb",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 12,
                color: "#555",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#999", marginBottom: 4, letterSpacing: "0.2px" }}>서버 안내문</div>
              {task.error_message}
            </div>
          )}

          {/* 원인 요약 */}
          <p style={{ margin: "14px 0 4px", fontSize: 13, color: "#444", lineHeight: 1.55, wordBreak: "keep-all" }}>{guide.summary}</p>

          {/* 단계 */}
          <div style={{ marginTop: 14, fontSize: 13.5, fontWeight: 700, color: "#1a1a1a" }}>이렇게 해 보세요</div>
          <ol className="excel-fix-guide-steps">
            {guide.steps.map((step, i) => (
              <li key={i}>
                {typeof step === "string" ? step.replace(/고객센터/g, supportLabel()) : step}
                {extraSheetFigure && i === 0 && <SheetSelectFigure />}
                {Figure && guide.figureAfterStep === i + 1 && <Figure />}
              </li>
            ))}
          </ol>
          {Figure && !guide.figureAfterStep && <Figure />}

          {/* 고객센터 */}
          <div
            style={{
              marginTop: 6,
              marginBottom: 8,
              padding: "9px 12px",
              borderRadius: 10,
              background: "#E3F2FD",
              color: "#0D47A1",
              fontSize: 12,
              lineHeight: 1.5,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              wordBreak: "keep-all",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
              <circle cx="7" cy="7" r="6" fill="none" stroke="#1976D2" strokeWidth="1.4" />
              <path d="M7 6.2V10M7 4.2v.2" stroke="#1976D2" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <span>
              그래도 안 되면 <b>{supportLabel()}</b>에 문의해 주세요 — <b>파일명과 위 서버 안내문</b>을 함께 알려 주시면 빨리 찾을 수 있습니다.
            </span>
          </div>
        </div>

        {/* 푸터 버튼 */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 8,
            padding: "12px 20px 16px",
            borderTop: "1px solid #f0f0f0",
            background: "#fafafa",
          }}
        >
          {guide.linkTo === "payment" && (
            <button
              type="button"
              className="excel-fix-guide-btn excel-fix-guide-btn-primary"
              onClick={() => {
                onClose();
                history.push(pages.payment);
              }}
            >
              이용권 보러 가기
            </button>
          )}
          {canRetrySameFile && (
            <button
              type="button"
              className="excel-fix-guide-btn excel-fix-guide-btn-accent"
              onClick={() => {
                onRetry && onRetry();
                onClose();
              }}
            >
              같은 파일로 다시 실행
            </button>
          )}
          <button type="button" className="excel-fix-guide-btn excel-fix-guide-btn-ghost" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default ExcelFixGuideModal;
